// @ts-strict-ignore
import React from 'react'
import { UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, ComboboxItem, Input } from '@node-space/ui-components'
import { AddCustomerFormErrors, AddCustomerPayload } from 'types/customers'

interface CustomerAddressProps {
  title: string
  errors?: AddCustomerFormErrors
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  isLocalBeneficiary?: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  countryCode?: string
  countries?: ComboboxItem[]
  isCountriesLoading?: boolean
  stateCode?: string
  states?: ComboboxItem[]
  isStatesLoading?: boolean
  isIndividual?: boolean
  register?: UseFormRegister<AddCustomerPayload>
  setValue?: (name: string, value: string) => void
  renderCurrencySpecificFields?: () => React.ReactNode
}

export const CustomerAddress = ({
  title,
  errors,
  addressLine1,
  addressLine2,
  city,
  postalCode,
  countryCode,
  countries,
  isCountriesLoading,
  stateCode,
  states,
  isStatesLoading,
  register,
  setValue,
}: CustomerAddressProps) => {
  const { t } = useTranslation()
  const isUSCustomer = countryCode === 'US'

  const handleCountrySelect = (countryCode: string) => {
    const countryName = countries?.find(country => country?.value === countryCode)
      ?.leftText as string
    setValue('countryCode', countryCode)
    setValue('countryName', countryName)

    if (countryCode !== 'US') {
      setValue('stateCode', '')
      setValue('stateName', '')
    }
  }

  const handleStateSelect = (stateCode: string) => {
    const stateName = states?.find(state => state?.value === stateCode)?.leftText as string
    setValue('stateCode', stateCode)
    setValue('stateName', stateName)
  }

  return (
    <Box flexCol gapY={16}>
      <h2 className="text-h2 text-foreground-prominent">{title}</h2>

      <Combobox
        {...register('countryCode')}
        items={countries}
        loading={isCountriesLoading}
        label={t('customers.address.country')}
        searchPlaceholder={t('searchOrSelectACountry')}
        selectPlaceholder={t('searchOrSelectACountry')}
        onChange={handleCountrySelect}
        id="countryCode"
        errorText={errors?.countryCode?.message}
        defaultValue={countryCode}
      />

      {isUSCustomer && (
        <Combobox
          {...register('stateCode')}
          items={states}
          loading={isStatesLoading}
          disabled={isStatesLoading}
          label={t('customers.address.state')}
          searchPlaceholder={t('searchOrSelectAState')}
          selectPlaceholder={t('searchOrSelectAState')}
          onChange={handleStateSelect}
          id="stateCode"
          errorText={errors?.stateCode?.message}
          defaultValue={stateCode}
        />
      )}

      <Input
        {...register('addressLine1')}
        label={t('addressLine1')}
        inputMode="text"
        name="addressLine1"
        testId="business-address-line-1"
        errorText={errors?.addressLine1?.message}
        error={!!errors?.addressLine1}
        value={addressLine1}
        onChange={e => setValue('addressLine1', e?.target?.value)}
      />

      <Input
        {...register('addressLine2')}
        label={t('formFields.addressLine2')}
        inputMode="text"
        name="addressLine2"
        testId="business-address-line-2"
        errorText={errors?.addressLine2?.message}
        error={!!errors?.addressLine2}
        value={addressLine2}
        optionalText={t('optional')}
        onChange={e => setValue('addressLine2', e?.target?.value)}
      />

      <Box flex gapX={16}>
        <Box flexSize="auto" width="full">
          <Input
            {...register('city')}
            label={t('customers.address.city')}
            inputMode="text"
            name="city"
            testId="customer-city"
            errorText={errors?.city?.message}
            error={!!errors?.city}
            value={city}
            onChange={e => setValue('city', e?.target?.value)}
          />
        </Box>
        <Box flexSize="auto" width="full">
          <Input
            {...register('postalCode')}
            label={t('customers.address.postalCode')}
            inputMode="text"
            name="postalCode"
            testId="customer-postCode"
            errorText={errors?.postalCode?.message}
            error={!!errors?.postalCode}
            value={postalCode}
            onChange={e => setValue('postalCode', e?.target?.value)}
          />
        </Box>
      </Box>
    </Box>
  )
}
